import React, { useEffect } from 'react';
import i18next from 'i18next';
import i18nextHttpBackend from 'i18next-http-backend';
import { I18nextProvider } from 'react-i18next';
import { useSelector } from 'react-redux';
import { PreferenceActions, PreferenceSections, usePreferences } from 'utils/preferences';
import { getAuthUser } from 'store/auth/selectors';
import {
  APP_THEME,
  applyThemePreference,
  getStoredThemePreference,
  normalizeThemePreference,
  persistThemePreference,
} from 'utils/theme-preference';
import { getLocale } from '../store/app/selectors';

import {
  buildI18nResources,
  DEFAULT_LOCALE,
  getLocaleLayoutDirection,
  getLocaleTextDirection,
  resolveLocale,
  SUPPORTED_INTL_LOCALES,
  toIntlLocale,
} from './locales';
import { setMomentLocale } from './momentLocale';
import { arabicNumeralsPostProcessor } from './arabicNumeralsPostProcessor';

const DEFAULT_INTL_LOCALE = toIntlLocale(DEFAULT_LOCALE);

if (__BROWSER__) {
  i18next.use(i18nextHttpBackend);
}

i18next.use(arabicNumeralsPostProcessor);

// i18next.use(__BROWSER__ ? i18nextXHRBackend : {}).init({
i18next.init(
  {
    backend: {
      // for all available options read the backend's repository readme file
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    react: {
      // Must be false until Suspense is supported on the server side
      useSuspense: __BROWSER__,
    },
    // Keep false unless debugging i18n; true floods the console on every languageChanged.
    debug: false,
    load: 'currentOnly',
    supportedLngs: SUPPORTED_INTL_LOCALES,
    // Always fall back to English for missing keys / unsupported languages
    fallbackLng: DEFAULT_INTL_LOCALE,
    lng: DEFAULT_INTL_LOCALE,
    fallbackNS: ['translation'],
    interpolation: {
      // Allow `/` and other characters in formatted dates and numbers (otherwise e.g. 22/03/2026 becomes 22&#x2F;03&#x2F;2026 in the UI)
      escapeValue: false,
    },
    postProcess: ['arabicNumerals'],
    // This option is necessary to tell i18next to try loading missing resources via
    // i18next-http-backend, otherwise no calls will be made if resources are defined.
    partialBundledLanguages: true,
    resources: buildI18nResources(),
    parseMissingKeyHandler: (missing: string) => {
      if (process.env.NODE_ENV === 'development' && __BROWSER__) {
        console.warn('MISSING TRANSLATION:', missing);
      }
      return missing;
    },
  }
);

i18next.languages = SUPPORTED_INTL_LOCALES;

type I18NProps = {
  children: React.ReactNode;
};

const I18N: React.FC<I18NProps> = ({ children }) => {
  const locale = useSelector(getLocale);
  const currentUser = useSelector(getAuthUser);
  const preferences = usePreferences();
  const userUuid = currentUser?.userUuid ?? '';

  useEffect(() => {
    const intlLocale = toIntlLocale(locale);
    if (i18next.language !== intlLocale) {
      i18next.changeLanguage(intlLocale);
    }
    if (__BROWSER__) {
      const layoutDirection = getLocaleLayoutDirection(locale);
      const textDirection = getLocaleTextDirection(locale);
      document.documentElement.lang = intlLocale;
      document.documentElement.dir = layoutDirection;
      document.documentElement.classList.toggle(
        'locale-ar',
        resolveLocale(locale) === 'ar'
      );
      document.documentElement.dataset.textDirection = textDirection;
    }
    setMomentLocale(intlLocale);
  }, [locale]);

  useEffect(() => {
    // Pre-auth: localStorage preference (default light). Signed-in: account pref.
    if (!userUuid) {
      applyThemePreference(getStoredThemePreference());
      return;
    }
    const themePreference = normalizeThemePreference(
      preferences(
        PreferenceActions.GET,
        PreferenceSections.APP,
        { name: APP_THEME },
        userUuid
      )
    );
    applyThemePreference(themePreference);
    persistThemePreference(themePreference);
  }, [preferences, userUuid, currentUser?.prefDetails?.[1]]);

  return <I18nextProvider i18n={i18next}>{children}</I18nextProvider>;
};

export default React.memo(I18N);
